import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import dayjs from 'dayjs';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Tooltip } from 'antd';

import SelectWithFetchNext from '../../../../../shared/components/select-with-fetch-next';
import * as optionsConstants from '../../../../../shared/constants/lookupListConstants';
import * as sharedActions from '../../../../../shared/store/actions';
import { RootState } from '../../../../../store';
import { AllInsuredNumbersFilter } from '../../../models';

import './styles.less';
import { TagCategory } from '../../../../../shared/models/tag-category';

const { RangePicker } = DatePicker;

type SearchInsuredNumbersProps = {
  setFilter: React.Dispatch<React.SetStateAction<AllInsuredNumbersFilter>>;
};

const SearchInsuredNumbers = ({ setFilter }: SearchInsuredNumbersProps) => {
  const [filterForm] = Form.useForm<AllInsuredNumbersFilter>();
  const didMount = useRef(false);

  const allInsuredNumbersLoading = useSelector((state: RootState) => state.smses.allInsuredNumbersLoading);

  useEffect(() => {
    if (!didMount.current) {
      setFilter({} as AllInsuredNumbersFilter);

      didMount.current = true;
    }
  }, []);

  const handleApplySearch = () => {
    if (!allInsuredNumbersLoading) {
      filterForm.validateFields().then((formData) => {
        setFilter(formData);
      });
    }
  };

  const handleClear = () => {
    filterForm.resetFields();
    setFilter({} as AllInsuredNumbersFilter);
  };

  const onSelect = (formItemName: string, valueObject: { key: string; value: string }[]) => {
    const newData = {} as AllInsuredNumbersFilter;
    const values = valueObject.map((x) => x.key);

    (newData[formItemName as keyof AllInsuredNumbersFilter] as string[]) = values;

    filterForm.setFieldsValue(newData);
  };

  const getFields = () => {
    return (
      <>
        <Col span={6}>
          <Form.Item
            name={['nameString']}
            label={
              <div>
                Names, DBA, Phones, Emails
                <Tooltip placement="top" title={'Search by Name, DBA, Insured ID, Customer ID, E-mail, Phone, Cell Phone, Fax.'}>
                  <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                </Tooltip>
              </div>
            }
          >
            <Input placeholder="Names, DBA, Phones, Emails" />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['createDate']} label={'Create Date (from-to)'}>
            <RangePicker
              allowEmpty={[true, true]}
              style={{ width: '100%' }}
              allowClear
              size="small"
              format={'MM/DD/YYYY'}
              placeholder={['Date From:', 'Date To:']}
              presets={[
                { label: 'Today', value: [dayjs(), dayjs()] },
                {
                  label: 'This Month',
                  value: [dayjs().startOf('month'), dayjs().endOf('month')],
                },
              ]}
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={['policyNumberString']}
            label={
              <div>
                Policy Number
                <Tooltip placement="top" title={'Search by Policy Number of Insured.'}>
                  <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                </Tooltip>
              </div>
            }
          >
            <Input placeholder="Policy Number" />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={['locationsString']}
            label={
              <div>
                Locations
                <Tooltip placement="top" title={'Search in Locations by names, phones, addresses.'}>
                  <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                </Tooltip>
              </div>
            }
          >
            <Input placeholder="Locations" />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['insuredUserAccountType']} label={'Has User Account'}>
            <Select
              allowClear
              showSearch
              suffixIcon={null}
              placeholder="Has User Account"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children ? option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : false
              }
            >
              {optionsConstants.hasUserAccountOpts.map((opt) => (
                <Select.Option key={opt.key} value={opt.value} title={opt.text}>
                  {opt.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name={['contactsString']}
            label={
              <div>
                Contacts
                <Tooltip placement="top" title={'Search in Contacts by names, phones.'}>
                  <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                </Tooltip>
              </div>
            }
          >
            <Input placeholder="Contacts" />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['greetingName']} label={'Greeting Name'}>
            <Input placeholder="Greeting Name" />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['typeOfBusiness']} label={'Type Of Business'}>
            <Select
              allowClear
              showSearch
              placeholder="Type Of Business"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children ? option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : false
              }
            >
              {optionsConstants.truckingCompanyTypeOfBusiness.map((opt) => (
                <Select.Option key={opt.key} value={opt.value} title={opt.text}>
                  {opt.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['active']} label={'Active'}>
            <Select
              allowClear
              showSearch
              placeholder="Active"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children ? option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : false
              }
            >
              {optionsConstants.activeOpts.map((opt) => (
                <Select.Option key={opt.key} value={opt.value} title={opt.text}>
                  {opt.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['locationIds']} label={'Location'}>
            <SelectWithFetchNext
              onSelect={(valueObject) => onSelect('locationIds', valueObject)}
              dropdownMatchSelectWidth={false}
              fetchNext={sharedActions.getLocations}
              placeholder="Select Location"
              onEnterPress={handleApplySearch}
              multipleMode
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['agentIds']} label={'Agent'}>
            <SelectWithFetchNext
              onSelect={(valueObject) => onSelect('agentIds', valueObject)}
              dropdownMatchSelectWidth={false}
              fetchNext={sharedActions.getAgentsLookup}
              placeholder="Select Agent"
              onEnterPress={handleApplySearch}
              multipleMode
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['csrManagers']} label={'Account Manager / CSR'}>
            <SelectWithFetchNext
              onSelect={(valueObject) => onSelect('csrManagers', valueObject)}
              dropdownMatchSelectWidth={false}
              fetchNext={sharedActions.getAgentsLookup}
              placeholder="Select Account Manager / CSR"
              onEnterPress={handleApplySearch}
              multipleMode
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['referralSources']} label={'Referral Source'}>
            <SelectWithFetchNext
              onSelect={(valueObject) => onSelect('referralSources', valueObject)}
              dropdownMatchSelectWidth={false}
              fetchNext={sharedActions.getReferralSources}
              placeholder="Select Referral Source"
              onEnterPress={handleApplySearch}
              multipleMode
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['tags']} label={'Tag'}>
            <SelectWithFetchNext
              onSelect={(valueObject) => onSelect('tags', valueObject)}
              dropdownMatchSelectWidth={false}
              fetchNext={sharedActions.getTags}
              placeholder="Select Tag"
              onEnterPress={handleApplySearch}
              multipleMode
              customRender={(tags: TagCategory[]) => {
                return tags && tags.length
                  ? tags.map((tagCategory) => {
                      return (
                        <Select.OptGroup key={tagCategory.id} label={tagCategory.name}>
                          {tagCategory.tags
                            ? tagCategory.tags.map((tag) => (
                                <Select.Option value={tag.id} key={tag.id} item_object={{ ...tag, key: tag.id }}>
                                  {tag.name}
                                </Select.Option>
                              ))
                            : null}
                        </Select.OptGroup>
                      );
                    })
                  : null;
              }}
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name={['stateIds']} label={'State'}>
            <SelectWithFetchNext
              onSelect={(valueObject) => onSelect('stateIds', valueObject)}
              dropdownMatchSelectWidth={false}
              fetchNext={sharedActions.getStates}
              placeholder="Select State"
              onEnterPress={handleApplySearch}
              multipleMode
            />
          </Form.Item>
        </Col>
      </>
    );
  };

  return (
    <Card className="search-insured-numbers-panel" title="Search">
      <Form form={filterForm} size="small" className="sms-insureds-filter-form" layout="vertical">
        <Row gutter={24}>{getFields()}</Row>

        <Row>
          <Col span={24} className="insureds-filter-form-buttons">
            <Button type="primary" ghost htmlType="button" onClick={() => handleApplySearch()} loading={allInsuredNumbersLoading}>
              Search
            </Button>
            <Button type="primary" ghost style={{ marginLeft: '8px' }} onClick={() => handleClear()}>
              Clear
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default SearchInsuredNumbers;
